
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PriceHistoryChart } from './PriceHistoryChart';
import { publicFeatures } from '../config/publicFeatures';
import { supabase } from '../lib/supabaseClient';
import { RelatedProducts } from './RelatedProducts';
import { NotFoundPage } from './NotFoundPage';
import { ModernHeader } from './ui/ModernHeader';
import { useWishlist } from '../contexts/WishlistContext';
import { StickyProductActions } from './ui/StickyProductActions';
import { ExitIntentModal } from './ui/ExitIntentModal';
import { useBehavioralUX } from '../hooks/useBehavioralUX';
import { getProductTitle, getProductFeatures, getProductDescription } from '../utils/dataFallbacks';

// New focused components
import { ProductHeader } from './product/ProductHeader';
import { ProductImageGallery } from './product/ProductImageGallery';
import { ProductPriceSection } from './product/ProductPriceSection';
import { ProductTabs } from './product/ProductTabs';
import { ProductSchema } from './product/ProductSchema';

async function fetchProduct(asin) {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('asin', asin)
    .single();

  if (error) {
    console.error('Product fetch error:', error);
    if (error.code === 'PGRST116') {
      return null;
    }
    throw new Error(error.message);
  }
  
  return data;
}

export function ProductDetail() {
  const { asin } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('features');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPriceHistoryModal, setShowPriceHistoryModal] = useState(false);
  const [showWishlistToast, setShowWishlistToast] = useState(false);

  // Behavioral UX hooks
  const { showStickyElements, exitIntentShown, resetExitIntent } = useBehavioralUX();

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', asin],
    queryFn: () => fetchProduct(asin),
    enabled: !!asin,
    retry: (failureCount, error) => {
      if (error?.message?.includes('PGRST116')) {
        return false;
      }
      return failureCount < 2;
    }
  });

  const {
    isWishlisted,
    toggleWishlist,
    wishlistLoaded
  } = useWishlist();

  if (isLoading) return <div className="p-8 text-center">Loading product...</div>;
  if (!product) return <NotFoundPage />;
  if (error) return <div>Error loading product.</div>;

  // Wait for wishlist to load before showing wishlist state
  const wishlisted = wishlistLoaded ? isWishlisted(product.asin) : false;

  const handleWishlistToggle = () => {
    toggleWishlist(product);
    setShowWishlistToast(true);
    setTimeout(() => setShowWishlistToast(false), 3000);
  };

  const handleShare = () => {
    const currentUrl = window.location.href;

    if (navigator.share) {
      navigator.share({
        title: product?.title,
        text: `Check out this product: ${product?.title}`,
        url: currentUrl,
      });
    } else {
      navigator.clipboard.writeText(currentUrl);
      setShowShareModal(true);
      setTimeout(() => setShowShareModal(false), 2000);
    }
  };

  const partnerTag = import.meta.env.VITE_AMAZON_PARTNER_TAG;
  const amazonUrl = `https://www.amazon.com/dp/${asin}?tag=${partnerTag}`;

  const handleAmazonClick = () => {
    window.open(amazonUrl, '_blank');
  };

  const hasPriceHistory = publicFeatures.priceHistory && Array.isArray(product.price_history) && product.price_history.length > 0;

  // Loading state
  if (isLoading) {
    return (
      <>
        <ModernHeader />
        <div className="min-h-screen bg-gray-50 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="grid grid-cols-2 gap-8">
                <div className="h-96 bg-gray-200 rounded"></div>
                <div className="space-y-4">
                  <div className="h-8 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  <div className="h-12 bg-gray-200 rounded"></div>
                  <div className="h-24 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <>
        <ModernHeader />
        <NotFoundPage />
      </>
    );
  }

  return (
    <>
      <ModernHeader />
      <ProductSchema product={product} />

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4 md:p-8">
            {/* Enhanced Breadcrumbs with Schema.org markup */}
            <nav className="mb-4" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400" itemScope itemType="https://schema.org/BreadcrumbList">
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <button onClick={() => navigate('/')} className="hover:text-gray-700 dark:hover:text-gray-300" itemProp="item">
                    <span itemProp="name">Home</span>
                  </button>
                  <meta itemProp="position" content="1" />
                </li>
                {product.category_slug && (
                  <>
                    <li aria-hidden="true">•</li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                      <button onClick={() => navigate(`/category/${product.category_slug}`)} className="hover:text-gray-700 dark:hover:text-gray-300" itemProp="item">
                        <span itemProp="name">{product.category || product.category_slug}</span>
                      </button>
                      <meta itemProp="position" content="2" />
                    </li>
                  </>
                )}
                <li aria-hidden="true">•</li>
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <button onClick={() => navigate(`/search/${encodeURIComponent(product.brand)}`)} className="hover:text-gray-700 dark:hover:text-gray-300" itemProp="item">
                    <span itemProp="name">{product.brand}</span>
                  </button>
                  <meta itemProp="position" content={product.category_slug ? "3" : "2"} />
                </li>
                <li aria-hidden="true">•</li>
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" className="text-gray-700 dark:text-gray-200 font-medium truncate max-w-xs">
                  <span itemProp="name">{getProductTitle(product)}</span>
                  <meta itemProp="position" content={product.category_slug ? "4" : "3"} />
                </li>
              </ol>
            </nav>

            {/* SEO: H1 for Product Page */}
            <h1 className="sr-only">{getProductTitle(product)} - Price Tracking & Reviews</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Product Image */}
              <ProductImageGallery product={product} />

              {/* Product Info */}
              <div className="space-y-6">
                <ProductHeader
                  product={product}
                  isWishlisted={wishlisted}
                  onWishlistToggle={handleWishlistToggle}
                  onShare={handleShare}
                  showWishlistToast={showWishlistToast}
                  wishlistLoaded={wishlistLoaded}
                />

                <ProductPriceSection
                  product={product}
                  onAmazonClick={handleAmazonClick}
                  onPriceHistoryClick={() => setShowPriceHistoryModal(true)}
                  hasPriceHistory={hasPriceHistory}
                />
              </div>
            </div>

            {/* Product Details Tabs */}
            <ProductTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              product={product}
            />

            {/* Price History */}
            {hasPriceHistory && (
              <div className="mt-12">
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Price History</h2>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                  <div className="h-[400px]">
                    <PriceHistoryChart asin={asin} />
                  </div>
                </div>
              </div>
            )}

            {/* Related Products */}
            <RelatedProducts 
              asin={asin}
              features={getProductFeatures(product)}
              category={product.category_slug}
              brand={product.brand}
              title={getProductTitle(product)}
            />
          </div>
        </div>
      </div>

      {/* Sticky Actions */}
      <StickyProductActions
        product={product}
        isVisible={showStickyElements}
        onWishlistToggle={handleWishlistToggle}
        isWishlisted={wishlisted}
        onShare={handleShare}
      />

      {/* Exit Intent Modal (price-alert email capture) */}
      {publicFeatures.priceAlerts && (
        <ExitIntentModal
          isOpen={exitIntentShown}
          onClose={resetExitIntent}
          product={product}
        />
      )}

      {/* Share Modal Feedback */}
      {showShareModal && (
        <div className="fixed top-4 right-4 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50">
          Link copied to clipboard!
        </div>
      )}
    </>
  );
}
